
import AppSendMessage from "@/components/mail/SendMessageDialog.vue";
import { Contact } from "@/models/base";
import { ContactAddress } from "@/models/base";
import { ReminderConfig } from "@/models/dossier-configs/reminder-config";
import { Reminder } from "@/models/reminder";
import { ReminderEmailTemplate } from "@/models/reminder-email-template";
import { ReminderTemplate } from "@/models/reminder-template";
import { Sale } from "@/models/base";
import { DossierConfigService } from "@/services/dossier-config-service";
import { ReminderService } from "@/services/reminder-service";
import { DocumentLayoutInfo } from "@/utils/documents/layout-info";
import Vue from "vue";
import AppReminderSelectDocuments from "./ReminderSelectDocuments.vue";
import AppReminderSelectTemplates from "./ReminderSelectTemplates.vue";
import { CustomerService } from "../../services/customer-service";
import { differenceInMonths } from "date-fns";
import { IMail, ISendMessageDialogContact } from "@/utils/mail";
import { AuthService } from "@/services/auth-service";
import { Tier } from "@/utils/payments/tiers";
import { MailSettings } from "@/models/mail-settings";
import { v4 } from "uuid";
import { downloadBlob } from "@/utils/blob";
import { printPdf } from "@/utils/pdf";
import { MainSocketService, UserConfigService } from "@/services";
import { PrintContext } from "@/utils/sale-layouts/print-settings";


export default Vue.extend({
	data() {
		return {
			step: 1,
			reminders: [] as Reminder[],
			contact: new Contact(),
			whichReminder: 1,
			config: DossierConfigService.getReminderConfig(),
			templates: [] as ReminderTemplate[],
			layouts: [] as DocumentLayoutInfo[],
			content: "",
			emailTemplates: [] as ReminderEmailTemplate[],
			emailContent: "",
			emailSubject: "",
			emailRecipient: "",
			emailAttachements: [] as File[],
			selectedAddress: null as null | ContactAddress,
			selectedEmailTemplate: null as null | ReminderEmailTemplate,
			selectedTemplate: null as null | ReminderTemplate,
			selectedLayout: null as null | DocumentLayoutInfo,
			reminderSaved: false,
			addReadConfirm: false,
			selectedReminders: [] as Reminder[],
			downloadProgress: undefined as undefined | number,
			downloadingEventId: v4()
		};
	},
	props: {
		contactId: {
			type: Number,
			required: true
		}
	},
	computed: {
		emailContacts(): ISendMessageDialogContact[]{
			return this.contact.getSendMessageDialogContacts();
		},
		selectedSales(): Sale[] {
			return this.selectedReminders.map(r => r.Sale || new Sale());
		},
		includeSalesInReminders():boolean {
			return DossierConfigService.getReminderConfig().IncludeSales;
		},
		includeSalesInRemindersForPrint():boolean {
			return DossierConfigService.getReminderConfig().IncludeSalesForPrint || false;
		},
		isDownloading():boolean{
			return this.downloadProgress !== undefined;
		}
	},
	watch: {
		selectedReminders: {
			handler(){
				if (this.step != 1) return;
				this.selectedTemplate = null;
				this.selectedEmailTemplate = null;
				for (let reminder of this.selectedReminders) {
					if (!reminder.Sale) continue;
					let sale = reminder.Sale;
					if (!this.selectedTemplate || sale.Reminder + 1 > this.selectedTemplate.ReminderID) {
						this.selectedTemplate = this.templates.find(t => t.ReminderID == (sale.Reminder === 4 ? 4 : sale.Reminder + 1)) as ReminderTemplate;
					}
					if (!this.selectedEmailTemplate || sale.Reminder + 1 > this.selectedEmailTemplate.ReminderCount) {
						this.selectedEmailTemplate = this.emailTemplates.find(t => t.ReminderCount == (sale.Reminder === 4 ? 4 : sale.Reminder + 1)) as ReminderEmailTemplate;
					}
				}
			}
		},
		selectedAddress() {
			if (!this.selectedAddress) return;
			if (this.selectedAddress.EmailSettings.DefaultForRemindersMailIndex != null) {
				this.emailRecipient = this.selectedAddress.getEmailByIndex(this.selectedAddress.EmailSettings.DefaultForRemindersMailIndex);
			}
			if (!this.emailRecipient && this.selectedSales.length > 0){
				this.emailRecipient = this.selectedAddress.getEmailForSaleTypeId(this.selectedSales[0].TypeID);
			}
			if (!this.emailRecipient) {
				this.emailRecipient = this.selectedAddress.getDefaultEmail();
			}
		},
		selectedTemplate(value: ReminderTemplate) {
			if (!value) return;
			let lang = "nl-be";
			if (this.contact.MainAddress) {
				lang = this.contact.MainAddress.Lng;
			}
			if (this.selectedAddress) {
				lang = this.selectedAddress.Lng;
			}
			this.content = value.Content.getTranslation(lang);
			this.emailSubject = value.Title.getTranslation(lang);
		},
		downloadingEventId: {
			immediate: true,
			handler(newEventId:string, oldEventId:string){
				MainSocketService.off(this.getSocketEventName(oldEventId), this.onProgress);
				MainSocketService.off(this.getSocketFileIdEventName(oldEventId), this.onFileId);
				MainSocketService.on(this.getSocketEventName(newEventId), this.onProgress);
				MainSocketService.on(this.getSocketFileIdEventName(newEventId), this.onFileId);
			}
		},
		step(){
			if (this.step > 2) return;
			for (let reminder of this.selectedReminders){
				reminder.FileID = 0;
			}
		}
	},
	methods: {
		getSocketEventName(id:string){
			return `/reminder-layout/progress/${id}`;
		},
		getSocketFileIdEventName(id:string){
			return `/reminder-layout/file-id/${id}`;
		},
		onProgress(val:number){
			this.downloadProgress = val * 100;
		},
		cancel() {
			this.$emit("cancel");
		},
		canUseReadNotifications() {
			return AuthService.wfCompany.Tier >= Tier.PROFESSIONAL;
		},
		async openSendModal() {
			if (!this.selectedEmailTemplate) {
				this.step = 3;
				return;
			}
			if (!this.selectedAddress) return;
			if (!this.selectedLayout) return;
			if (!this.selectedTemplate) return;
			if (this.isDownloading) return;
			let file: any = await this.downloadLayout(PrintContext.EMAIL_SEND);
			this.emailAttachements = [file];
			this.emailContent = await ReminderService.computeEmailTemplate(this.selectedEmailTemplate, this.contact, this.selectedReminders);
			this.step = 3;
		},
		async downloadLayout(createContext:PrintContext):Promise<File>{
			if (!this.selectedAddress) throw new Error("No address selected");
			if (!this.selectedLayout) throw new Error("No layout selected");
			if (!this.selectedTemplate) throw new Error("No template selected");
			if (this.isDownloading) throw new Error("Already downloading");
			this.downloadProgress = 0;
			try{
				let blob = await ReminderService.downloadReminder(
					this.selectedReminders,
					this.selectedAddress,
					this.selectedLayout.ID,
					this.selectedTemplate.ID,
					this.content,
					this.includeSalesInReminders,
					this.downloadingEventId,
					createContext);
				let file = new File([blob], ReminderService.getPdfName(), {type: "application/pdf"});
				return file;
			}catch(err){
				throw err;
			}finally{
				this.downloadProgress = undefined;
				this.downloadingEventId = v4();
			}
		},
		async downloadDocument() {
			if (!this.selectedAddress) return;
			if (!this.selectedLayout) return;
			if (!this.selectedTemplate) return;
			if (this.isDownloading) return;
			let file = await this.downloadLayout(PrintContext.DOWNLOAD);
			downloadBlob(file, file.name);
			for (let reminder of this.reminders){
				reminder.IsDownloaded = true;
			}
			await this.saveReminder();
			this.$emit("close");
		},
		async printDocument() {
			if (!this.selectedAddress) return;
			if (!this.selectedLayout) return;
			if (!this.selectedTemplate) return;
			let file = await this.downloadLayout(PrintContext.PRINT);
			await printPdf(file, file.name);
			for (let reminder of this.reminders){
				reminder.IsPrinted = true;
			}
			await this.saveReminder();
			this.$emit("close");
		},
		async saveReminder() {
			if (!this.reminderSaved) {
				this.selectedReminders.map((r:Reminder)=>{
					if(this.selectedTemplate && r.Sale){
						if(r.ReminderID<this.selectedTemplate.ReminderID){
							r.ReminderID = this.selectedTemplate.ReminderID;
						}
					}
					return r;
				});
				this.selectedReminders = await ReminderService.postReminders(this.selectedReminders);
				this.reminderSaved = true;
			}
		},
		async sendHandler(mailData:IMail, settings:MailSettings){
			await this.saveReminder();
			await ReminderService.sendReminders(this.selectedReminders, mailData, settings, this.addReadConfirm && this.canUseReadNotifications());
		},
		async onMessageSent() {
			if (UserConfigService.getReminderSettings().PrintReminderAfterSend){
				for (let attachment of this.emailAttachements){
					printPdf(attachment, attachment.name);
				}
			}
			this.$emit("close", this.selectedReminders.map(s=>s.SaleID));
		},
		makeReminders(sales: Sale[]) {
			this.reminders = sales.map(s => {
				if (s.Reminder > this.whichReminder) {
					this.whichReminder = s.Reminder;
				}

				let reminder = new Reminder();
				reminder.Cost = 0;
				reminder.Sale = s;
				reminder.SaleID = s.ID;
				reminder.ContactID = s.ContactID;
				reminder.BookYear = s.BookYear;

				if (this.config && this.config.AutoCalculateCosts) {
					let cost = (this.config["HCost" + (s.Reminder < 4 ? s.Reminder + 1 : 4) as keyof ReminderConfig] as number);
					reminder.Cost = cost;
					let costPerc = (this.config["HPerc" + (s.Reminder < 4 ? s.Reminder + 1 : 4) as keyof ReminderConfig] as number);
					if (costPerc) {
						reminder.Cost = (reminder.Sale.TotalOpen / 100) * costPerc;
					}
					let intrests = this.config["Intrests"] as number;
					if (intrests) {
						let diff = differenceInMonths(new Date(),reminder.Sale.ExpirationDate);
						diff = diff < 1 ? 1 : diff;
						reminder.Interest = ((reminder.Sale.TotalOpen / 100) * intrests) * diff;
					}
				}

				return reminder;
			});
			this.selectedReminders.push(...this.reminders);
		},
		onFileId(id:number){
			for (let reminder of this.selectedReminders){
				reminder.FileID = id;
			}
		}
	},
	async mounted() {
		this.contact = await CustomerService.getContact(this.contactId);
		let sales = await ReminderService.getSales(this.contact.ID);
		this.templates = await ReminderService.getReminderTemplates();
		this.emailTemplates = await ReminderService.getEmailTemplates();
		this.layouts = await ReminderService.getReminderLayouts();
		this.selectedAddress = this.contact.MainAddress || null;
		this.selectedEmailTemplate = this.emailTemplates[0] || null;
		this.selectedLayout = this.layouts[0] || null;



		this.makeReminders(sales);
	},
	destroyed(){
		MainSocketService.off(this.getSocketEventName(this.downloadingEventId), this.onProgress);
		MainSocketService.off(this.getSocketFileIdEventName(this.downloadingEventId), this.onFileId);
	},
	components: {
		AppReminderSelectDocuments,
		AppReminderSelectTemplates,
		AppSendMessage
	},
	i18nEx: {
		componentPrefix: "components.sales"
	}
});
